// ** React Imports
import { useCallback, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";

// ** Redux & Store & Actions
import { useSelector } from "react-redux";

// ** Third Party Components
import { useTranslation } from "react-i18next";

// ** Custom Hooks
import useOpenGraphMetaTags from "hooks/useOpenGraphMetaTags";

// ** Utils & Helpers
import toTitleCase from "utils/toTitleCase";
import generatePath from "utils/generatePath";
import { generateMetaDescriptionOnUserProfile } from "utils/generateMetaDescription";
import { GENDER_TEXT } from "helpers/constants";

// ** Config
import configGlobal from "configuration";

// ** Routes
import containers from "containers";

// #####################################################

const useUserMetaTags = () => {
	const { t } = useTranslation("common", "filtersAndSelectors");

	const user = useSelector((state) => state.userProfileMain);
	const userLoaded = useSelector((state) => state.userProfileMain.loaded);

	// ** Hook - useLocation
	const location = useLocation();

	// ** Odczytanie parametrów z adresu URL
	const { login } = useParams();

	// ** Login użytkownika z adresu URL
	const userLoginFromUrl = login?.toLowerCase();

	// ** Funkcja generuje link do profilu użytkownika.
	const userProfileLink = generatePath(containers.user.path, {
		login: userLoginFromUrl,
	});

	// ** SEO TITLE
	const title = useMemo(() => {
		return `${[
			userLoaded
				? toTitleCase(user.login || "")
				: toTitleCase(userLoginFromUrl || ""),
			user.sex && t(`sexOptions.${user.sex}`).toLocaleLowerCase(),
			user.sex
				? user.sex === GENDER_TEXT.COUPLE
					? user.partners.age[0] &&
					  user.partners.age[1] &&
					  `${user.partners.age[0]} ${t("common:years")}, ${
							user.partners.age[1]
					  } ${t("common:years")}`
					: user.partners.age[0] &&
					  `${user.partners.age[0]} ${t("common:years")}`
				: null,
			toTitleCase(user?.location?.city),
		]
			.filter(Boolean)
			.join(", ")}`;
	}, [userLoaded, userProfileLink]);

	// ** SEO DESCRIPTION
	const metaDescription = useMemo(() => {
		if (userLoaded) {
			return generateMetaDescriptionOnUserProfile(user, t);
		}
		return "";
	}, [userLoaded, userProfileLink]);

	// ** Open Graph Meta Tags
	const ogMetaTags = useOpenGraphMetaTags({
		customTitle: `${title} - ${t("common:titleTemplate")}`,
		customDescription: metaDescription,
		customUrl: `https://${configGlobal.host}/users/${userLoginFromUrl}`,
	});

	const canonicalLink = useCallback(() => {
		return (
			<link
				rel="canonical"
				href={`https://${configGlobal.host}/users/${userLoginFromUrl}`}
			/>
		);
	}, [location.pathname]);

	// #####################################################

	return { title, metaDescription, ogMetaTags, canonicalLink };
};

// #####################################################

export default useUserMetaTags;

// #####################################################
